import { FC, useCallback, useEffect, useMemo } from 'react';
import cls from 'classnames';
import { Avatar, Button, ButtonSize } from '@lemon8/design';
import { useAtom, useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';
import { $LandingModalState } from './atom';
import { useTranslation } from 'react-i18next';
import XMark from 'icon/x_mark.svg';
import { EClickPosition, EPageType } from 'shared/constant';
import { activeToCurrentPage } from '~/utils/active/active-to-current-page';
import { FixBodyUtil } from '~/utils/fix-body';
import { $SharerDetail, $SharingCurrentTraffic } from 'shared/atoms';
import { UserDetailUtil } from 'shared/models';
import './style.scss';

export const LandingModal: FC = () => {
  const [modalState, setLandingModalState] = useAtom($LandingModalState);
  const { open } = modalState;
  const { t } = useTranslation();
  const { pageType } = useAtomValue($SharingCurrentTraffic);

  const loadableSharerDetail = useAtomValue(loadable($SharerDetail));

  const avatarUrl = useMemo(() => {
    if (loadableSharerDetail.state === 'hasData' && UserDetailUtil.isUserAvailable(loadableSharerDetail.data)) {
      return loadableSharerDetail.data.avatar;
    }
    return '';
  }, [loadableSharerDetail]);

  useEffect(() => {
    if (open) {
      window.Tea('wap_pop_window_show', {
        current_position: EClickPosition.LandingModal,
      });
      FixBodyUtil.fix();
    } else {
      FixBodyUtil.restore();
    }
  }, [open]);

  const handleClickConfirm = useCallback(() => {
    window.Tea('wap_pop_window_click', {
      current_position: EClickPosition.LandingModal,
      click_result: 'install',
    });
    activeToCurrentPage(EClickPosition.LandingModal);
  }, []);

  const handleClickCancel = useCallback(() => {
    window.Tea('wap_pop_window_click', {
      current_position: EClickPosition.LandingModal,
      click_result: 'cancel',
    });
    setLandingModalState({ open: false, hasClosed: true });
  }, []);

  const visibleCls = useMemo(
    () => ({
      shown: open,
      hidden: !open,
    }),
    [open],
  );

  const modalContent = useMemo(() => {
    if (!open) {
      return null;
    }

    return (
      <div className="landing-modal-content">
        <div className="landing-modal-content-logo">
          {pageType === EPageType.USER ? (
            <Avatar className="avatar" src={avatarUrl} size={80} />
          ) : (
            <div className="lemon8-logo" />
          )}
        </div>
        <div className="text-wrapper">
          <div className="text-header">{t('landing_postDetail_modal_header')}</div>
          <div className="text-body">{t('landing_postDetail_modal_body')}</div>
        </div>
        <div className="cancel-x" onClick={handleClickCancel}>
          <XMark className="x-mark-icon" />
        </div>
        <Button
          className="install-button"
          size={ButtonSize.large}
          variant="Primary"
          title={t('landing_postDetail_modal_CTA')}
          onClick={handleClickConfirm}
        />
      </div>
    );
  }, [open]);

  return (
    <div className={cls('landing-modal', visibleCls)}>
      <div className={cls('landing-modal-mask', visibleCls)} />
      <div className={cls('landing-modal-content-anchor', visibleCls)}>
        <div className={cls('landing-modal-content', visibleCls)}>{modalContent}</div>
      </div>
    </div>
  );
};
